/**
 * The backend speaks two different error dialects and the UI must understand both.
 *
 *   HTTPException  ->  { "error": "device_not_found", "message": "Device not found" }
 *   Pydantic 422   ->  { "detail": [ { "loc": [...], "msg": "...", "type": "..." } ] }
 *
 * The first comes from the global handler in app/main.py; the second is FastAPI's
 * built-in validation handler, which that global handler does not cover. Anything
 * that only parses one shape renders "[object Object]" on the other.
 */

export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  /** snake_case code from the backend, e.g. `unable_to_claim_device`. */
  readonly code: string;
  /** Per-field issues from a 422. Empty for every other error. */
  readonly issues: FieldIssue[];

  constructor(status: number, code: string, message: string, issues: FieldIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
  }

  /**
   * A 401 means the session is gone. A 404 does not: ownership checks return 404
   * rather than 403 on purpose, so device ids stay non-enumerable. Treating those
   * as auth failures would log the user out for visiting a stale bookmark.
   */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

interface RawDetailItem {
  loc?: unknown[];
  msg?: string;
}

function fieldFromLoc(loc: unknown[] | undefined): string {
  if (!Array.isArray(loc)) return '';
  // loc is ["body", "field_name"] — the first segment is the request part.
  const parts = loc.filter((p): p is string => typeof p === 'string' && p !== 'body');
  return parts.join('.');
}

export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError(
      response.status,
      'unreadable_response',
      response.status === 0
        ? 'Could not reach the server.'
        : `Request failed (HTTP ${response.status}).`
    );
  }

  if (typeof body !== 'object' || body === null) {
    return new ApiError(response.status, 'unknown_error', `Request failed (HTTP ${response.status}).`);
  }

  const record = body as Record<string, unknown>;

  // Shape 1: the global HTTPException handler.
  if (typeof record.error === 'string' && typeof record.message === 'string') {
    return new ApiError(response.status, record.error, record.message);
  }

  // Shape 2: Pydantic validation errors.
  if (Array.isArray(record.detail)) {
    const issues: FieldIssue[] = (record.detail as RawDetailItem[])
      .map((item) => ({
        field: fieldFromLoc(item.loc),
        message: typeof item.msg === 'string' ? item.msg : 'Invalid value',
      }))
      .filter((issue) => issue.message.length > 0);

    const summary =
      issues.length === 1 && issues[0]
        ? capitalize(issues[0].message)
        : 'Some of the details you entered are not valid.';

    return new ApiError(response.status, 'validation_error', summary, issues);
  }

  // FastAPI also emits a bare string `detail` in a few places.
  if (typeof record.detail === 'string') {
    return new ApiError(response.status, toCode(record.detail), record.detail);
  }

  return new ApiError(response.status, 'unknown_error', `Request failed (HTTP ${response.status}).`);
}

function toCode(detail: string): string {
  return detail.toLowerCase().replace(/\s+/g, '_').replace(/\./g, '');
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Message for anything thrown out of a query or mutation, API error or not. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof TypeError) {
    // fetch() rejects with TypeError when the network or CORS blocks the request.
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
